import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { capture } from '@/lib/analytics';

export type ProjectsViewMode = 'grid' | 'table';

export const PROJECTS_VIEW_STORAGE_KEY = 'overlayqa.projects.viewMode';
const DEFAULT_VIEW: ProjectsViewMode = 'grid';

function parseViewMode(raw: string | null | undefined): ProjectsViewMode | null {
  if (raw === 'grid' || raw === 'table') return raw;
  return null;
}

function readStoredView(): ProjectsViewMode | null {
  try {
    return parseViewMode(localStorage.getItem(PROJECTS_VIEW_STORAGE_KEY));
  } catch {
    return null;
  }
}

function writeStoredView(view: ProjectsViewMode): void {
  try {
    localStorage.setItem(PROJECTS_VIEW_STORAGE_KEY, view);
  } catch {
    // localStorage may be unavailable (private mode, quota). Silent no-op is fine —
    // the URL still carries the choice for the current navigation.
  }
}

export interface UseProjectsViewModeResult {
  viewMode: ProjectsViewMode;
  setView: (next: ProjectsViewMode) => void;
}

export function useProjectsViewMode(): UseProjectsViewModeResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlView = parseViewMode(searchParams.get('view'));

  const [storedView, setStoredView] = useState<ProjectsViewMode | null>(() => readStoredView());

  const viewMode: ProjectsViewMode = urlView ?? storedView ?? DEFAULT_VIEW;

  const setView = useCallback(
    (next: ProjectsViewMode) => {
      if (next !== viewMode) {
        capture('projects_view_toggled', { from: viewMode, to: next });
      }
      setStoredView(next);
      writeStoredView(next);
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          if (next === DEFAULT_VIEW) {
            updated.delete('view');
          } else {
            updated.set('view', next);
          }
          return updated;
        },
        { replace: true },
      );
    },
    [setSearchParams, viewMode],
  );

  useEffect(() => {
    if (urlView && urlView !== storedView) {
      setStoredView(urlView);
      writeStoredView(urlView);
    }
  }, [urlView, storedView]);

  return { viewMode, setView };
}
